import { Formik } from 'formik';
import { useMemo } from 'react';
import { object, ObjectSchema, string } from 'yup';

import { CampusPlan, ContractData, InsuranceMetaGrouping } from '@uplift/api';
import { useIsMobile } from '@uplift/hooks';
import {
  useInsuranceCompaniesQuery, useNonPartnerInsuranceCompaniesQuery,
  useNonPartnerInsuranceCompanyQuery
} from '@uplift/queries';
import { Autocomplete, Button } from '@uplift/ui-v2';

import { FullScreenSelect } from '../../../components/full-screen-select';
import {
  StepContainer, StepContent, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../components';
import { OTHER_NON_PARTNER_INSURANCE_ID, SELF_PAY_ID } from '../../utils';
import { InsuranceNextData } from '../types';

export type InsuranceProps = {
  onNext: (data: InsuranceNextData) => void;
  onBack: () => void;
  orgContract: ContractData;
  insuranceId: string | null;
  stateId: string;
  title?: string;
  isBookingSponsored?: boolean;
};

type InsuranceFormValues = {
  insuranceId: string;
};

export const Insurance: React.VFC<InsuranceProps> = (props) => {
  const isMobile = useIsMobile();
  const planType = props.orgContract?.plan.type;
  const secondaryButtonText = planType === CampusPlan.lite ? 'I’ll pay for services myself' : 'I don’t have insurance';

  // Enabled if the booking is sponsored and we need to show a combined list of partner and non-partner insurance companies.
  const { data: nonPartnerInsuranceCompanies = [] } = useNonPartnerInsuranceCompaniesQuery(props.stateId, { enabled: props?.isBookingSponsored });

  // Enabled if the selected insurance is a non-partner insurance company.
  const { data: nonPartnerInsuranceCompany } = useNonPartnerInsuranceCompanyQuery(props.insuranceId, { enabled: !!props.insuranceId && !!nonPartnerInsuranceCompanies && nonPartnerInsuranceCompanies?.some(company => company.id === props.insuranceId) });

  // Determine whether the student selected 'Other' as their insurance and added a free-text insurance company.
  const isOtherNonPartnerInsuranceSelected = props.insuranceId === OTHER_NON_PARTNER_INSURANCE_ID || nonPartnerInsuranceCompany?.creatorType === 'student';

  const insuranceId = useMemo(() => {
    if (props.insuranceId === SELF_PAY_ID) {
      return null;
    }

    if (isOtherNonPartnerInsuranceSelected) {
      return OTHER_NON_PARTNER_INSURANCE_ID;
    }

    return props.insuranceId;
  }, [ isOtherNonPartnerInsuranceSelected, props.insuranceId ]);

  const onSubmit = (data: InsuranceFormValues) => {
    props.onNext(data);
  };

  return (
    <Formik
      initialValues={{ insuranceId: insuranceId }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnMount
    >
      {(formik) => (
        <StepContainer>
          <StepContent>
            <StepHeading>{props.title ?? 'Do you have insurance you can use?'}</StepHeading>
            <StepForm onSubmit={formik.handleSubmit} maxWidth="384px">
              {isMobile ? (
                <MobileInsuranceSelectInput stateId={props.stateId} isBookingSponsored={props.isBookingSponsored} />
              ) : (
                <DesktopInsuranceSelectInput stateId={props.stateId} isBookingSponsored={props.isBookingSponsored}/>
              )}
            </StepForm>
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton disabled={!formik.isValid} />
            <Button color="secondary" onClick={() => props.onNext({ insuranceId: SELF_PAY_ID })} data-testid="no-insurance-btn">
              {secondaryButtonText}
            </Button>
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

const validationSchema = (): ObjectSchema<InsuranceFormValues> =>
  object({
    insuranceId: string().nullable().required('Please select an insurance').defined(),
  });

const OTHER_INSURANCE_OPTION = {
  id: OTHER_NON_PARTNER_INSURANCE_ID,
  name: 'Other — My insurance isn’t listed',
  metaGrouping: 'other'
} as const;

const prepareInsuranceCompanies = (companies: any[] = [], nonPartnerCompanies: any[] = [], isBookingSponsored?: boolean) => {
  const adminAddedNonPartnerInsuranceCompanies = nonPartnerCompanies?.filter(company => company.creatorType === 'admin');
  const nonPartnerCompaniesWithGrouping = adminAddedNonPartnerInsuranceCompanies?.map(company => ({
    ...company,
    metaGrouping: 'nonPartner' as unknown as InsuranceMetaGrouping,
  }));

  const allCompanies = isBookingSponsored
    ? [ ...companies, ...nonPartnerCompaniesWithGrouping ]
    : companies;

  // Only add Other option when booking is sponsored by school
  return isBookingSponsored ? [ ...allCompanies, OTHER_INSURANCE_OPTION ] : allCompanies;
};

const getInsuranceGroupLabel = (metaGrouping: InsuranceMetaGrouping | 'other' | string, isSponsored?: boolean) => {
  switch (metaGrouping) {
    case InsuranceMetaGrouping.medicaid:
      return 'Medicaid';
    case InsuranceMetaGrouping.medicare:
      return 'Medicare';
    case 'other':
      return 'Other';
    case 'nonPartner':
    default:
      return isSponsored ? 'Insurance Carriers' : 'Uplift Insurance Partners';
  }
};

const getInsuranceSortOrder = (company: any) => {
  switch (company.metaGrouping) {
    case InsuranceMetaGrouping.medicare:
      return 2;
    case InsuranceMetaGrouping.medicaid:
      return 3;
    case 'other':
      return 4;
    default:
      return 1;
  }
};

const sortInsuranceCompanies = (companies: any[]) => {
  return companies.sort((a, b) => {
    const orderA = getInsuranceSortOrder(a);
    const orderB = getInsuranceSortOrder(b);

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // Put "Medicare (Original)" first in the Medicare section
    if (a.metaGrouping === InsuranceMetaGrouping.medicare &&
        b.metaGrouping === InsuranceMetaGrouping.medicare) {
      if (a.name === 'Medicare (Original)') return -1;
      if (b.name === 'Medicare (Original)') return 1;
    }

    return a.name.localeCompare(b.name);
  });
};

type InsuranceSelectProps = {
  stateId: string;
  isBookingSponsored?: boolean;
};

const DesktopInsuranceSelectInput: React.VFC<InsuranceSelectProps> = (props) => {
  const { data: companies = [] } = useInsuranceCompaniesQuery({ stateId: props.stateId });
  const { data: nonPartnerCompanies = [] } = useNonPartnerInsuranceCompaniesQuery(props.stateId, { enabled: props?.isBookingSponsored });

  const options = sortInsuranceCompanies(
    prepareInsuranceCompanies(companies, nonPartnerCompanies, props.isBookingSponsored)
  );

  return (
    <Autocomplete
      disableClearable
      fullWidth
      name="insuranceId"
      label="Your insurance"
      options={options}
      transformValue={(v) => v?.id}
      getOptionLabel={(opt) => opt?.name ?? ''}
      getOptionDisabled={(o) => !o?.id}
      groupBy={(option) => getInsuranceGroupLabel(option.metaGrouping, props.isBookingSponsored)}
    />
  );
};

const MobileInsuranceSelectInput: React.VFC<InsuranceSelectProps> = (props) => {
  const { data: companies = [] } = useInsuranceCompaniesQuery({ stateId: props.stateId });
  const { data: nonPartnerCompanies = [] } = useNonPartnerInsuranceCompaniesQuery(props.stateId, { enabled: props?.isBookingSponsored });

  const options = sortInsuranceCompanies(
    prepareInsuranceCompanies(companies, nonPartnerCompanies, props.isBookingSponsored)
  );

  return (
    <FullScreenSelect
      options={options}
      getLabel={(option) => option?.name || ''}
      getValue={(option) => option.id}
      name="insuranceId"
      label="Your insurance"
      groupBy={(option) => getInsuranceGroupLabel(option.metaGrouping, props.isBookingSponsored)}
    />
  );
};
